import { useState, useCallback } from 'react'
import { useTriggersQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import type { Trigger } from '@/types/compatibility'
import {
  BoltIcon,
  MagnifyingGlassIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'

interface TriggersViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onTriggerSelect?: (triggerName: string) => void
}

export function TriggersView({ connection, database, schema, onTriggerSelect }: TriggersViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  // const [sortBy, setSortBy] = useState<'name'>('name') // Removed as only one sort field
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const { data: triggersResponse, isLoading, error } = useTriggersQuery(
    connection.id,
    database,
    schema,
    { enabled: true, databaseType: connection.type === 'mysql' ? 'MySQL' : 'PostgreSQL' }
  )

  const allTriggers = Array.isArray(triggersResponse) ? triggersResponse : []

  const getTriggerName = (trigger: Trigger): string => {
    if (typeof trigger === 'string') return trigger
    if (trigger && typeof trigger === 'object') {
      const anyTrig = trigger as { name?: unknown; trigger_name?: unknown }
      const byName = typeof anyTrig.name === 'string' ? anyTrig.name : undefined
      const byAlt = typeof anyTrig.trigger_name === 'string' ? anyTrig.trigger_name : undefined
      return byName || byAlt || 'Unnamed Trigger'
    }
    return 'Unnamed Trigger'
  }

  const filteredAndSortedTriggers = allTriggers
    .filter((trigger: Trigger) => {
      if (!searchTerm) return true
      const triggerName = getTriggerName(trigger)
      return triggerName.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: Trigger, b: Trigger) => {
      const aValue = getTriggerName(a)
      const bValue = getTriggerName(b)
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = useCallback(() => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }, [sortOrder])

  const handleSortName = useCallback(() => { handleSort() }, [handleSort])
  
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handleTriggerSelect = useCallback((trigger: Trigger) => {
    if (!onTriggerSelect) return
    const name = getTriggerName(trigger)
    if (name) onTriggerSelect(name)
  }, [onTriggerSelect])

  const getSortIcon = () => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading triggers: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <BoltIcon className="w-6 h-6 mr-2 text-yellow-600" />
              Triggers
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedTriggers.length} triggers
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search triggers by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-8 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleSortName}
              >
                Trigger Name
                {getSortIcon()}
              </div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedTriggers.length === 0 ? (
              <div className="text-center py-12">
                <BoltIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No triggers match your criteria' : 'No triggers found'}
                </p>
              </div>
            ) : (
              filteredAndSortedTriggers.map((trigger: Trigger, index: number) => (
                <div key={getTriggerName(trigger) || `trigger-${index}`} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="col-span-8 flex items-center">
                    <BoltIcon className="w-4 h-4 text-yellow-600" />
                    <div className="ml-2">
                      <button type="button"
                        onClick={() => { handleTriggerSelect(trigger) }}
                        className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                        title="View trigger details"
                      >
                        {getTriggerName(trigger) || `Unnamed Trigger ${index + 1}`}
                      </button>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      onClick={() => { handleTriggerSelect(trigger) }}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="View definition"
                    >
                      <CodeBracketIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedTriggers.length} of {allTriggers.length} triggers
          </div>
        </div>
      </div>
    </div>
  )
}
